import { useEffect, useMemo, useState } from "react";
import styles from "./FacultyCardBasicInfo.module.scss";
import {
  Box,
  Card,
  CardContent,
  Collapse,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  getAuthorAwardsByID,
  getAuthorCollaboratorGraphById,
  getAuthorPublicationsByID,
} from "Explore/Services/Author";
import { LoadingListEntityProfile } from "Explore/Components";
import PublicationItem from "Explore/Components/PublicationItem";
import { Empty } from "antd";
import AwardItem from "Explore/Components/AwardItem";
import D3Graph from "Explore/Components/Graph";
import { IconPack } from "../../../Config/Icons";
import { InfoCircleOutlined } from "@ant-design/icons";
import { numberRegex } from "Explore/Utils/numberRegex";
import useDeviceCheck from "Hooks/useDeviceCheck";

const MAP_TAB_VALUES = Object.freeze({
  PUBLICATIONS: 1,
  GRANTS: 2,
  COLLABORATORS: 3,
  MORE_INFO: 4,
  VENUES: 5,
});

const TAB_VALUES = [
  {
    label: "Notable Scholarly Works",
    value: MAP_TAB_VALUES.PUBLICATIONS,
    icon: IconPack.unit_publications,
  },
  {
    label: "Notable Federal Grants",
    value: MAP_TAB_VALUES.GRANTS,
    icon: IconPack.grant,
  },
  {
    label: "Top Contributed Venues",
    value: MAP_TAB_VALUES.VENUES,
    icon: IconPack.venue_outline_black,
  },
  {
    label: "Collaborators",
    value: MAP_TAB_VALUES.COLLABORATORS,
    icon: IconPack.collaborator,
  },
];

const FacultyCardBasicInfo = ({ data }) => {
  const { isTablet } = useDeviceCheck();
  const [tabValue, setTabValue] = useState(false);
  const [publications, setPublications] = useState({
    data: [],
    isLoading: true,
  });
  const [grants, setGrants] = useState({ data: [], isLoading: true });
  const [collaborators, setCollaborators] = useState({
    data: [],
    isLoading: true,
  });
  const tabValues = useMemo(() => {
    let result = [...TAB_VALUES];
    if (
      (data.experience_metadata && !!data.experience_metadata.length) ||
      (data.keyword_specific_stats &&
        !!Object.keys(data.keyword_specific_stats).length)
    )
      result = [
        ...TAB_VALUES,
        {
          label: "More info",
          value: MAP_TAB_VALUES.MORE_INFO,
          icon: <InfoCircleOutlined />,
        },
      ];
    return result;
  }, [data.id]);

  useEffect(() => {
    if (tabValue === MAP_TAB_VALUES.PUBLICATIONS && publications.isLoading) {
      getAuthorPublicationsByID(data.id, 0, 3, null, [], false, (res) => {
        setPublications({ data: res.publications, isLoading: false });
      });
    }
  }, [tabValue, publications.isLoading]);
  useEffect(() => {
    if (tabValue === MAP_TAB_VALUES.GRANTS && grants.isLoading) {
      getAuthorAwardsByID(data.id, 0, 3, null, [], (res) => {
        setGrants({ data: res.awards, isLoading: false });
      });
    }
  }, [tabValue, grants.isLoading]);
  useEffect(() => {
    if (tabValue === MAP_TAB_VALUES.COLLABORATORS && collaborators.isLoading) {
      getAuthorCollaboratorGraphById({ id: data.id }).then((res) =>
        setCollaborators({ data: res.data, isLoading: false })
      );
    }
  }, [tabValue, collaborators.isLoading]);

  const handleChange = (_, newValue) => {
    setTabValue(newValue);
  };

  return (
    <div className={styles["basic-info"]}>
      <Tabs
        value={tabValue}
        onChange={handleChange}
        classes={{
          root: styles["tabs"],
          indicator: styles["tabs--indicator"],
        }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        {tabValues.map(({ label, value, icon }) => (
          <Tab
            key={value}
            label={label}
            value={value}
            icon={icon}
            iconPosition="start"
            onClick={() => value === tabValue && setTabValue(false)}
            classes={{
              root: styles["tabs--item"],
              selected: styles["selected"],
            }}
          />
        ))}
      </Tabs>

      <Collapse
        in={tabValue != false}
        classes={{ wrapperInner: styles["tabs--content"] }}
      >
        {tabValue === MAP_TAB_VALUES.PUBLICATIONS && (
          <LoadingListEntityProfile
            isLoading={publications.isLoading}
            component={
              publications.data?.length > 0 ? (
                publications.data.map((item, index) => {
                  return <PublicationItem key={index} data={item} />;
                })
              ) : (
                <Empty description="No Publication Found." />
              )
            }
            componentNum={3}
            avatar={true}
          />
        )}
        {tabValue === MAP_TAB_VALUES.GRANTS && (
          <LoadingListEntityProfile
            isLoading={grants.isLoading}
            component={
              grants.data?.length > 0 ? (
                grants.data.map((item, index) => {
                  return <AwardItem key={index} data={item} />;
                })
              ) : (
                <Empty description="No Award Found." />
              )
            }
            componentNum={3}
            avatar={true}
          />
        )}
        {tabValue === MAP_TAB_VALUES.VENUES && (
          <Box className={styles["venues-wrapper"]}>
            {(data.venues || []).slice(0, 5).map((venue) => (
              <Card key={venue.id} className={styles["venue-card"]}>
                <CardContent className={styles["card-content"]}>
                  <div className={styles["card-layout"]}>
                    <div className={styles["venue-info"]}>
                      <Typography
                        variant="body1"
                        component="h3"
                        className={styles["venue-name"]}
                      >
                        {venue.name}
                      </Typography>
                      <div className={styles["venue-stats"]}>
                        <Typography
                          variant="body2"
                          className={styles["stat-item"]}
                        >
                          H-Index: <strong>{venue.h_index}</strong>
                        </Typography>
                      </div>
                    </div>
                    <div className={styles["rank-display"]}>
                      <Typography
                        variant="body1"
                        component="div"
                        className={styles["rank-number"]}
                      >
                        #{venue.rank}
                      </Typography>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
        {tabValue === MAP_TAB_VALUES.COLLABORATORS && (
          <D3Graph
            simpleMode
            data={collaborators.data}
            isLoading={collaborators.isLoading}
            height={300}
          />
        )}
        {tabValue === MAP_TAB_VALUES.MORE_INFO && (
          <div className={styles["more-information"]}>
            {data.keyword_specific_stats &&
              !!Object.keys(data.keyword_specific_stats).length && (
                <div className={styles["more-information--body"]}>
                  <div className={styles["more-information--body--title"]}>
                    <InfoCircleOutlined />
                    <p>Keyword Statistics</p>
                  </div>
                  <ul className={styles["more-information--body--content"]}>
                    {Object.entries(data.keyword_specific_stats).map(
                      ([key, value]) => (
                        <li
                          className={
                            styles["more-information--body--content--item"]
                          }
                        >
                          <p>{key}</p>
                          <Grid container>
                            {value.publication_count && (
                              <Grid
                                item
                                xs={4}
                                className={
                                  styles[
                                    "more-information--body--content--item--statistics"
                                  ]
                                }
                              >
                                {IconPack.publications}
                                <p>
                                  <span>
                                    {numberRegex(value.publication_count)}
                                  </span>{" "}
                                  publications
                                </p>
                              </Grid>
                            )}
                            {value.citation_count && (
                              <Grid
                                item
                                xs={4}
                                className={
                                  styles[
                                    "more-information--body--content--item--statistics"
                                  ]
                                }
                              >
                                {IconPack.citation}
                                <p>
                                  <span>
                                    {numberRegex(value.citation_count)}
                                  </span>{" "}
                                  citations
                                </p>
                              </Grid>
                            )}
                            {value.h_index && (
                              <Grid
                                item
                                xs={4}
                                className={
                                  styles[
                                    "more-information--body--content--item--statistics"
                                  ]
                                }
                              >
                                {IconPack.h_index}
                                <p>
                                  <span>{numberRegex(value.h_index)}</span>{" "}
                                  h-index
                                </p>
                              </Grid>
                            )}
                          </Grid>
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}
          </div>
        )}
      </Collapse>
    </div>
  );
};

export default FacultyCardBasicInfo;
